
import React from 'react';
import { Trash2 } from "lucide-react";

const CancelConfirmationModal = ({ isOpen, onClose, onConfirm, orders, onDelete }) => {
    const [loading, setLoading] = React.useState(false);
    const [clientSearch, setClientSearch] = React.useState("");

    // Reset search whenever the modal opens/closes
    React.useEffect(() => {
        if (!isOpen) setClientSearch("");
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm();
        } catch (error) {
            console.error("Cancellation error:", error);
        } finally {
            // Only update state if still mounted
            if (isOpen) setLoading(false);
        }
    };

    const formatTime = (dateTimeStr) => {
        if (!dateTimeStr) return "-";
        try {
            const date = new Date(dateTimeStr);
            return date.toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        } catch (e) {
            return "-";
        }
    };

    // Filter rows for display only — cancel button still acts on ALL orders
    const filteredOrders = clientSearch.trim()
        ? orders.filter(o => {
            const cid = (o.clientId || o.client_id || "").toLowerCase();
            return cid.includes(clientSearch.trim().toLowerCase());
          })
        : orders;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm">
            <style>{`
                .modal-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .modal-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .modal-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 4px; }
                .modal-scrollbar::-webkit-scrollbar-thumb:hover { background: #6b7280; }
            `}</style>

            <div className="bg-[#14161f] rounded-lg border border-gray-700 w-full max-w-5xl shadow-2xl transform transition-all scale-100 opacity-100">

                {/* Header */}
                <div className="p-5 border-b border-gray-700/50 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Confirm Cancellation</h3>
                    <button
                        onClick={!loading ? onClose : undefined}
                        className={`text-gray-400 hover:text-white transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={loading}
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex flex-col gap-4 mb-6">

                        {/* Warning row */}
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                <span className="text-red-500 text-xl">⚠️</span>
                            </div>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Are you sure you want to cancel{" "}
                                <span className="text-white font-bold">{orders.length}</span>{" "}
                                order{orders.length > 1 ? 's' : ''}? This action is irreversible.
                            </p>
                        </div>

                        {/* Search bar */}
                        <div className="flex items-center gap-3">
                            <div className="relative max-w-xs w-full">
                                <svg
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search client ID..."
                                    value={clientSearch}
                                    onChange={e => setClientSearch(e.target.value)}
                                    className="w-full bg-[#0f1116] border border-gray-700 text-gray-200 text-xs px-3 py-1.5 pl-8 rounded focus:outline-none focus:ring-1 focus:ring-red-500/60 placeholder-gray-600"
                                />
                                {clientSearch && (
                                    <button
                                        onClick={() => setClientSearch("")}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {clientSearch.trim() && (
                                <span className="text-xs text-gray-500">
                                    Showing{" "}
                                    <span className="text-gray-300 font-semibold">{filteredOrders.length}</span>
                                    {" "}of {orders.length}
                                </span>
                            )}
                        </div>

                        {/* Orders table */}
                        <div className="max-h-[50vh] overflow-y-auto modal-scrollbar bg-gray-900/50 rounded border border-gray-800">
                            <table className="w-full text-left text-xs text-gray-400">
                                <thead className="bg-gray-800/90 text-gray-300 sticky top-0 font-medium z-10 backdrop-blur-sm">
                                    <tr>
                                        <th className="p-3 border-b border-gray-700">Client ID</th>
                                        <th className="p-3 border-b border-gray-700">Broker</th>
                                        <th className="p-3 border-b border-gray-700">Order ID</th>
                                        <th className="p-3 border-b border-gray-700">Symbol</th>
                                        <th className="p-3 border-b border-gray-700">Type</th>
                                        <th className="p-3 border-b border-gray-700">Qty</th>
                                        <th className="p-3 border-b border-gray-700">Time</th>
                                        {onDelete && <th className="p-3 border-b border-gray-700 text-right">Action</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.length === 0 ? (
                                        <tr>
                                            <td colSpan={onDelete ? 8 : 7} className="p-6 text-center text-gray-600">
                                                No orders match &ldquo;{clientSearch}&rdquo;
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredOrders.map((o, i) => (
                                            <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                                                <td className="p-3 font-medium text-gray-300">{o.clientId || o.client_id || "-"}</td>
                                                <td className="p-3">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                                        (o.broker || "").toLowerCase().includes("dhan")
                                                            ? "bg-emerald-900/50 text-emerald-400"
                                                            : (o.broker || "").toLowerCase().includes("zerodha")
                                                            ? "bg-orange-900/50 text-orange-400"
                                                            : "bg-blue-900/50 text-blue-400"
                                                    }`}>
                                                        {(o.broker || "FYERS").toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-3 font-mono opacity-80">{o.order_id || o.orderId || o.appOrderID}</td>
                                                <td className="p-3 text-white font-medium">{o.symbol || "-"}</td>
                                                <td className="p-3">{o.type || "LIMIT"}</td>
                                                <td className="p-3 text-white">{o.qty}</td>
                                                <td className="p-3 text-gray-300 font-mono text-xs">{formatTime(o.orderdatetime)}</td>
                                                <td className="p-3 text-right">
                                                    {onDelete && (
                                                        <button
                                                            onClick={() => onDelete(o.order_id || o.orderId || o.appOrderID)}
                                                            className="p-1.5 hover:bg-red-500/20 text-gray-500 hover:text-red-500 rounded-md transition-all group"
                                                            title="Remove from list"
                                                        >
                                                            <Trash2 size={15} className="group-hover:stroke-2" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-2">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className={`px-4 py-2 rounded text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            No, Keep
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            className={`px-4 py-2 rounded text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-900/20 transition-all transform hover:scale-105 flex items-center gap-2 ${loading ? 'opacity-80 cursor-not-allowed scale-100' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Cancelling...
                                </>
                            ) : (
                                `Yes, Cancel ${orders.length} Order${orders.length > 1 ? 's' : ''}`
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CancelConfirmationModal;
